import { useState, useRef } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { cn } from '@/lib/utils';
import { massMigrationApi, massMigrationKeys } from '@/lib/api/massMigration';
import type { MassMigrationBatchDto, MassMigrationBatchOrgDto } from '@/types/massMigration';
import { StatTile } from './StatTile';
import { OrgStatusBadge } from './OrgStatusBadge';
import { OrgDetailView } from './OrgDetailView';
import { InviteDrainRow, InviteCount } from './InviteDrainRow';

// ─── InFlightView ─────────────────────────────────────────────────────────────

type OrgFilter = 'All' | 'Running' | 'Failed' | 'Complete';

interface InFlightViewProps {
  batch: MassMigrationBatchDto;
}

export function InFlightView({ batch }: InFlightViewProps) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const failedSectionRef = useRef<HTMLDivElement>(null);

  const [orgFilter, setOrgFilter] = useState<OrgFilter>('All');
  const [selectedOrg, setSelectedOrg] = useState<MassMigrationBatchOrgDto | null>(null);

  const progressPct =
    batch.totalOrgs > 0 ? Math.round((batch.succeeded / batch.totalOrgs) * 100) : 0;

  const completedOrgs = batch.succeeded;

  const filteredOrgs: MassMigrationBatchOrgDto[] = batch.orgs.filter((org) => {
    if (orgFilter === 'All') return true;
    if (orgFilter === 'Running') return org.status === 'InProgress';
    if (orgFilter === 'Failed') return org.status === 'Failed';
    if (orgFilter === 'Complete') return org.status === 'Succeeded';
    return true;
  });

  const { mutate: cancelBatch, isPending: isCancelling } = useMutation({
    mutationFn: () => massMigrationApi.cancelBatch(batch.id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: massMigrationKeys.batches() });
      void queryClient.invalidateQueries({ queryKey: massMigrationKeys.batch(batch.id) });
      void navigate({ to: '/migrations' });
    },
  });

  function handleFailedTileClick() {
    setOrgFilter('Failed');
    setTimeout(() => {
      failedSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 50);
  }

  const orgFilters: OrgFilter[] = ['All', 'Running', 'Failed', 'Complete'];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-wrap items-center gap-3">
        <h2 className="text-xl font-semibold">{batch.name}</h2>
        <div className="inline-flex items-center gap-1.5">
          <span
            className="inline-block h-2.5 w-2.5 rounded-full bg-info animate-pulse"
            aria-hidden="true"
          />
          <Badge variant="info">In Progress</Badge>
        </div>
        <span className="text-sm text-muted-foreground">
          Started {new Date(batch.createdAt).toLocaleString()}
        </span>
      </div>

      {/* Progress card */}
      <div className="rounded-lg border border-border bg-card p-5 space-y-3">
        <div className="flex items-baseline justify-between">
          <span className="text-base font-medium">
            {completedOrgs} of {batch.totalOrgs} organizations complete
          </span>
          <span className="text-sm text-muted-foreground">{progressPct}%</span>
        </div>
        <Progress value={progressPct} className="h-3" aria-label="Batch progress" />
      </div>

      {/* Stat tiles */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <StatTile label="Total Orgs" value={batch.totalOrgs} />
        <StatTile label="Complete" value={batch.succeeded} variant="success" />
        <StatTile
          label="Failed"
          value={batch.failed}
          variant={batch.failed > 0 ? 'destructive' : 'default'}
          onClick={batch.failed > 0 ? handleFailedTileClick : undefined}
          clickable={batch.failed > 0}
        />
        <StatTile label="Running" value={batch.inProgress} variant="info" />
      </div>

      <InviteDrainRow batch={batch} />

      {/* Org table */}
      <div ref={failedSectionRef} className="space-y-3">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm font-medium">Organizations</span>
          <div className="flex gap-1 ml-auto" role="group" aria-label="Filter organizations">
            {orgFilters.map((f) => (
              <button
                key={f}
                type="button"
                onClick={() => setOrgFilter(f)}
                className={cn(
                  'rounded-full px-3 py-1 text-xs font-medium transition-colors',
                  orgFilter === f
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-muted text-muted-foreground hover:bg-muted/80',
                )}
                aria-pressed={orgFilter === f}
              >
                {f}
              </button>
            ))}
          </div>
        </div>

        <div className="rounded-md border border-border overflow-hidden">
          <table className="w-full text-sm">
            <caption className="sr-only">Organizations in this batch</caption>
            <thead>
              <tr className="border-b border-border bg-muted/30">
                <th className="px-4 py-2.5 text-left text-xs font-medium text-muted-foreground">
                  Organization
                </th>
                <th className="px-4 py-2.5 text-left text-xs font-medium text-muted-foreground">
                  Bridge ID
                </th>
                <th className="px-4 py-2.5 text-left text-xs font-medium text-muted-foreground">
                  Status
                </th>
                <th className="px-4 py-2.5 text-left text-xs font-medium text-muted-foreground">
                  Modules
                </th>
                <th className="px-4 py-2.5 text-left text-xs font-medium text-muted-foreground">
                  Emailed Invites
                </th>
              </tr>
            </thead>
            <tbody>
              {filteredOrgs.length === 0 ? (
                <tr>
                  <td
                    colSpan={5}
                    className="px-4 py-8 text-center text-sm text-muted-foreground"
                  >
                    No organizations match this filter.
                  </td>
                </tr>
              ) : (
                filteredOrgs.map((org) => (
                  <tr
                    key={org.migrationId}
                    className="border-b border-border last:border-0 cursor-pointer hover:bg-muted/50 focus-within:bg-muted/50 transition-colors"
                    onClick={() => setSelectedOrg(org)}
                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setSelectedOrg(org); } }}
                    tabIndex={0}
                    role="button"
                    aria-label={`View details for ${org.organizationName}`}
                  >
                    <td className="px-4 py-3 font-medium">{org.organizationName}</td>
                    <td className="px-4 py-3 font-mono text-xs text-muted-foreground">
                      {org.bridgeEntityUuid}
                    </td>
                    <td className="px-4 py-3">
                      <OrgStatusBadge status={org.status} />
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex flex-wrap gap-1">
                        {org.moduleKeys.map((key) => (
                          <span
                            key={key}
                            className="inline-flex items-center rounded border border-border bg-muted px-1.5 py-0.5 text-xs text-muted-foreground"
                          >
                            {key}
                          </span>
                        ))}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm tabular-nums">
                      <InviteCount org={org} />
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Cancel action — a batch reopened by a per-org retry keeps its completedAt; cancelling
          it would fail the running retry and lock the whole batch as Cancelled. */}
      {batch.completedAt === null && (
        <div className="flex justify-end border-t border-border pt-4">
          <Button
            variant="destructive"
            onClick={() => cancelBatch()}
            disabled={isCancelling}
          >
            {isCancelling ? 'Cancelling…' : 'Cancel batch'}
          </Button>
        </div>
      )}

      {/* Org detail dialog */}
      <Dialog open={selectedOrg !== null} onOpenChange={(open) => { if (!open) setSelectedOrg(null); }}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="sr-only">Organization details</DialogTitle>
          </DialogHeader>
          {selectedOrg && (
            <OrgDetailView
              org={selectedOrg}
              batchName={batch.name}
              onBackToBatch={() => setSelectedOrg(null)}
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
